<template>
  <long-query-layout
    :resource-name="resourceName"
    :query-filter="queryFilter"
    :map-query-filter-for-api="mapQueryFilterForApi"
    :apply-query-filter="applyQueryFilter"
    :result-fetch-url.sync="resultTable.url"
    :columns.sync="resultTable.columns"
    :group-headers.sync="resultTable.groupHeaders"
    :active-tab.sync="activeTab"
    :excel-properties-handler="excelPropertiesHandler.bind(this)"
    :convert-menu-default-query-filter="convertMenuDefaultQueryFilter"
    @result-area-size-change="updateTableSize"
    @query-completed="onQueryCompleted"
    @query-id-change="queryId = $event"
  >
    <!-- -------------------------------------------------------- -->
    <!-- ヘッダのアクション -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:header-actions="slotProps">
      <gl-switch
        :value="isDetail"
        :disabled="!(slotProps.queryId && activeTab == 'result')"
        @input="toggleDetail"
      >
        期間展開
      </gl-switch>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析条件 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:filter>
      <!-- 期間 -->
      <query-filter-element id="term" title="期間" contentWidth="medium">
        <filter-term v-bind.sync="queryFilter.term" :default-week-count="8" />
      </query-filter-element>

      <!-- 商品分類 -->
      <query-filter-element id="item" title="商品分類">
        <gl-select-company :company.sync="queryFilter.item.company" />
        <div>
          <md-radio v-model="queryFilter.item.selectMode" value="category">
            分類
            <gl-sub-text>で指定</gl-sub-text>
          </md-radio>
          <md-radio v-model="queryFilter.item.selectMode" value="item">
            商品
            <gl-sub-text>で指定</gl-sub-text>
          </md-radio>
        </div>
        <filter-item-category
          v-if="queryFilter.item.selectMode === 'category'"
          v-bind.sync="queryFilter.item"
          :pos-restriction="true"
        />
        <filter-item
          v-if="queryFilter.item.selectMode === 'item'"
          v-bind.sync="queryFilter.item"
          :pos-restriction="true"
        />
      </query-filter-element>

      <!-- 店舗 -->
      <query-filter-element id="store" title="店舗">
        <filter-store v-bind.sync="queryFilter.store" />
      </query-filter-element>

      <!-- オプション -->
      <query-filter-element id="option-pos" title="オプション">
        <filter-option-pos v-bind.sync="queryFilter.option" />
      </query-filter-element>
    </template>

    <!-- -------------------------------------------------------- -->
    <!-- 分析結果 -->
    <!-- -------------------------------------------------------- -->
    <template v-slot:result>
      <pos-transition-dialog
        :open.sync="transitionOpen"
        :trigger="transitionTriggerEvent"
        :title="transitionData.title"
        :destinations="destinations"
        @click="transition"
      ></pos-transition-dialog>
      <gl-table ref="myTable" v-bind="resultTable" />
    </template>
  </long-query-layout>
</template>

<script>
// 分析条件, 分析結果のためのレイアウト
import LongQueryLayout from '@/components/layout/LongQueryLayout.vue'
// 分析条件
import QueryFilterElement from '@/components/QueryFilterElement.vue'
import FilterTerm from '@/components/FilterTerm.vue'
import FilterItemCategory from '@/components/FilterItemCategory.vue'
import FilterItem from '@/components/FilterItem2.vue'
import FilterStore from '@/components/FilterStore.vue'
import FilterOptionPos from '@/components/FilterOptionPos.vue'
// 分析条件モデル
import term from '@/queryfilter/term'
import item from '@/queryfilter/item2'
import store from '@/queryfilter/store'
// 分析結果
import GlTable from '@/components/GlTable.vue'
import PosTransitionDialog from '@/components/PosTransitionDialog.vue'

export default {
  components: {
    QueryFilterElement,
    FilterTerm,
    FilterItem,
    FilterItemCategory,
    FilterStore,
    FilterOptionPos,
    LongQueryLayout,
    GlTable,
    PosTransitionDialog,
  },
  data() {
    return {
      resourceName: 'chocostick-h',
      /**
       * 表示タブ（分析条件、分析結果）
       *
       * 表示タブをプログラム側から切り替えたい場合をこれを使います。
       */
      activeTab: undefined,
      /**
       * 分析条件
       */
      queryFilter: {
        term: { order: 1, ...term.get() },
        item: {
          order: 2,
          summaryKey: 'itemGroup',
          enableFilterNewItems: true,
          ...item.get(),
        },
        store: { order: 3, ...store.get() },
        option: {
          order: 4,
          summaryKey: 'optionPos',
          evaluations: [
            'amount',
            'qty',
            'amount_per_qty',
            'qty_pi',
            'amount_pi',
          ],
          visibleEvaluations: [
            'amount',
            'qty',
            'amount_per_qty',
            'qty_pi',
            'amount_pi',
            'receipt_count',
            'sku_count',
            'store_count',
            'avg_ymd',
            'last_sale_ymd',
          ],
          errors: [],
        },
      },
      /**
       * 分析結果
       */
      resultTable: {
        columns: undefined,
        groupHeaders: undefined,
        sortColumnName: 'code',
        sortOrder: 'asc',
        url: undefined,
        width: 0,
        height: 0,
        customFormatter: {
          // linkify: (rowData, cell, value, colName) => {
          linkify: (rowData, cell) => {
            cell.classList.add('link-cell')
            cell.addEventListener('click', e => {
              this.transitionData.title =
                rowData.date === '合計'
                  ? rowData.date_from + '~' + rowData.date_to
                  : rowData.date
              this.transitionData.dateFrom = rowData.date_from
              this.transitionData.dateTo = rowData.date_to
              this.transitionTriggerEvent = e
              this.transitionOpen = true
            })
          },
        },
      },
      /**
       * 期間展開の有無
       */
      isDetail: false,
      /**
       * この画面が持つ分析条件を、クエリのペイロードに変換する
       */
      mapQueryFilterForApi: queryFilter => ({
        term: term.mapToApiPayload(queryFilter.term),
        item: item.mapToApiPayload(queryFilter.item),
        store: store.mapToApiPayload(queryFilter.store),
        option: {
          evaluations: queryFilter.option.evaluations,
        },
      }),
      /**
       * 渡された分析条件をこの画面の分析条件に反映する
       */
      applyQueryFilter: queryFilter => {
        term.applyPayload(this.queryFilter.term, queryFilter.term)
        const p = item.applyPayload(this.queryFilter.item, queryFilter.item)
        store.applyPayload(this.queryFilter.store, queryFilter.store)
        this.queryFilter.option.evaluations = queryFilter.option.evaluations

        return [p]
      },
      /**
       * Excel出力のパラメータ生成
       */
      excelPropertiesHandler() {
        const { name, order } = this.$refs.myTable.getCurrentSort()
        return {
          sortColumnName: name,
          sortOrder: order,
          showDetail: this.isDetail,
        }
      },
      transitionOpen: false,
      transitionTriggerEvent: undefined,
      transitionData: {
        dateFrom: undefined,
        dateTo: undefined,
      },
    }
  },
  computed: {
    destinations() {
      if (this.queryFilter.term.termType === 'd') {
        return [
          {
            resourceName: 'chocostick-i',
            label: '商品xKPI',
          },
          {
            resourceName: 'chocostick-s',
            label: '店舗xKPI',
          },
          {
            resourceName: 'chocostick-m',
            label: 'メーカーxKPI',
          },
        ]
      } else {
        return [
          {
            resourceName: 'chocostick-i',
            label: '商品xKPI',
          },
          {
            resourceName: 'chocostick-s',
            label: '店舗xKPI',
          },
          {
            resourceName: 'chocostick-t',
            label: '期間xKPI (日別)',
          },
          {
            resourceName: 'chocostick-m',
            label: 'メーカーxKPI',
          },
        ]
      }
    },
  },
  methods: {
    /**
     * 結果表示テーブルのサイズを更新する
     */
    updateTableSize(size) {
      this.resultTable.width = size.width
      this.resultTable.height = size.height - 50
    },
    toggleDetail(value) {
      this.isDetail = value

      if (this.$refs.myTable.c3table) {
        // 期間展開をON/OFFした際にソート順をキープします
        // キープはできるのですが、テーブルの表示上は▲▼アイコンは
        // 出ません（テーブルライブラリの仕様上…）。
        const { name, order } = this.$refs.myTable.getCurrentSort()
        this.resultTable.sortColumnName = name
        this.resultTable.sortOrder = order
      }

      if (this.isDetail) {
        this.resultTable.url += '?showDetail=true'
      } else {
        if (
          this.resultTable.url &&
          this.resultTable.url.lastIndexOf('?showDetail=true') > -1
        ) {
          this.resultTable.url = this.resultTable.url.substring(
            0,
            this.resultTable.url.lastIndexOf('?showDetail=true'),
          )
        }
      }
    },
    /**
     * エクセルデータダウンロード
     */
    async downloadExcel() {
      const { name, order } = this.$refs.myTable.getCurrentSort()
      const url = await this.$longQuery.getExcelUrl(
        'chocostick-h',
        this.queryId,
        name,
        order,
      )
      window.open(url)
    },
    onQueryCompleted() {
      this.toggleDetail(this.isDetail)
    },
    // デフォルトQueryFilterの変換処理
    // 商品グループは一時的なものなのでメニューのデフォルト条件
    // としては保持しない。
    convertMenuDefaultQueryFilter(queryFilter) {
      queryFilter.item.itemGroupId = undefined
      return queryFilter
    },
    async transition(destination) {
      const override = {
        immediate: true,
        term: {
          termType:
            destination === 'chocostick-t'
              ? 'd'
              : this.queryFilter.term.termType,
          dateFrom: this.transitionData.dateFrom.replaceAll('-', ''),
          dateTo: this.transitionData.dateTo.replaceAll('-', ''),
        },
      }
      const url = `${process.env.BASE_URL}query-${destination}?reference=${
        this.queryId
      }&override=${JSON.stringify(override)}`
      window.open(url)
    },
  },
}
</script>
